import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth, canWrite, isSuperAdmin } from "@/lib/auth";
import { toast } from "sonner";
import { InlineDeleteButton } from "@/components/InlineDeleteButton";
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { LineChart, Plus, RefreshCw, Search, ShieldAlert, UserCheck, Trash2 } from "lucide-react";

// ---------------------------------------------------------------------------
// MP12 Module 5C — Market-Signal / Catalogue-Gap workspace.
// Manual entry first. Every record is human-authored (origin='human'). Any
// future system suggestion must ship with origin='system_suggestion' and
// appear visibly distinct.
// ---------------------------------------------------------------------------

function OriginBadge({ value }) {
  const human = (value || "human") === "human";
  return (
    <Badge
      variant="outline"
      className="text-[10px] uppercase tracking-[0.14em] font-semibold"
      style={human ? { background: "hsl(210 45% 94%)", color: "hsl(210 55% 30%)", borderColor: "hsl(210 45% 74%)" }
                   : { background: "hsl(40 55% 94%)",  color: "hsl(35 60% 28%)",  borderColor: "hsl(40 55% 76%)" }}
      data-testid="ms-origin-badge"
    >
      {human ? "Human-entered" : "System suggestion"}
    </Badge>
  );
}

function CreateSignalDialog({ vocab, onCreated }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    kind: "MARKET_SIGNAL", signal_summary: "", source: "", source_date: "", confidence: "MEDIUM",
    genre: "", style: "", mood: "", instrumentation: "", use_case_relevance: "",
    observed_demand: "", catalogue_gap: "", recommendation: "", notes: "",
  });

  const submit = async () => {
    if (!form.signal_summary.trim()) { toast.error("Signal summary is required."); return; }
    if (!form.source.trim()) { toast.error("Source is required for a reviewed market signal."); return; }
    if (!form.source_date.trim()) { toast.error("Source date is required."); return; }
    setSaving(true);
    try {
      const r = await api.post("/market-signals", form);
      onCreated?.(r.data);
      toast.success("Market signal recorded.");
      setOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Create failed.");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="ms-new" className="gap-2"><Plus className="w-4 h-4" /> New signal</Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl" data-testid="ms-new-dialog">
        <DialogHeader>
          <DialogTitle>New Market Signal / Catalogue Gap</DialogTitle>
          <DialogDescription>Manual entry only. No live external data feeds required. All records land with origin “human”.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Kind</Label>
            <Select value={form.kind} onValueChange={(v)=>setForm({...form, kind: v})}>
              <SelectTrigger data-testid="ms-kind"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(vocab.kinds || []).map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Confidence</Label>
            <Select value={form.confidence} onValueChange={(v)=>setForm({...form, confidence: v})}>
              <SelectTrigger data-testid="ms-confidence"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(vocab.confidence || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <Label>Signal summary <span className="text-destructive">*</span></Label>
            <Textarea rows={2} value={form.signal_summary} onChange={(e)=>setForm({...form, signal_summary: e.target.value})} data-testid="ms-summary" />
          </div>
          <div>
            <Label>Source <span className="text-destructive">*</span></Label>
            <Input value={form.source} onChange={(e)=>setForm({...form, source: e.target.value})} data-testid="ms-source" />
          </div>
          <div>
            <Label>Source date <span className="text-destructive">*</span></Label>
            <Input value={form.source_date} onChange={(e)=>setForm({...form, source_date: e.target.value})} placeholder="2026-05-01" data-testid="ms-source-date" />
          </div>
          <div><Label>Genre</Label><Input value={form.genre} onChange={(e)=>setForm({...form, genre: e.target.value})} /></div>
          <div><Label>Style</Label><Input value={form.style} onChange={(e)=>setForm({...form, style: e.target.value})} /></div>
          <div><Label>Mood</Label><Input value={form.mood} onChange={(e)=>setForm({...form, mood: e.target.value})} /></div>
          <div><Label>Instrumentation</Label><Input value={form.instrumentation} onChange={(e)=>setForm({...form, instrumentation: e.target.value})} /></div>
          <div className="col-span-2"><Label>Use-case relevance</Label><Input value={form.use_case_relevance} onChange={(e)=>setForm({...form, use_case_relevance: e.target.value})} placeholder="sync / release / library" /></div>
          <div className="col-span-2"><Label>Observed demand</Label><Textarea rows={2} value={form.observed_demand} onChange={(e)=>setForm({...form, observed_demand: e.target.value})} /></div>
          <div className="col-span-2"><Label>Catalogue gap</Label><Textarea rows={2} value={form.catalogue_gap} onChange={(e)=>setForm({...form, catalogue_gap: e.target.value})} /></div>
          <div className="col-span-2"><Label>Recommendation</Label><Textarea rows={2} value={form.recommendation} onChange={(e)=>setForm({...form, recommendation: e.target.value})} /></div>
          <div className="col-span-2"><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e)=>setForm({...form, notes: e.target.value})} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={()=>setOpen(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="ms-new-save">{saving ? "Saving…" : "Create"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function MarketSignalsDesk() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [kind, setKind] = useState("");
  const [confidence, setConfidence] = useState("");
  const [vocab, setVocab] = useState({});
  const mayWrite = canWrite(user);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (q.trim()) params.q = q.trim();
      if (kind) params.kind = kind;
      if (confidence) params.confidence = confidence;
      const r = await api.get("/market-signals", { params });
      setRows(r.data?.rows || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Load failed.");
    } finally { setLoading(false); }
  }, [q, kind, confidence]);

  useEffect(() => {
    api.get("/market-signals/meta/vocab").then((r)=>setVocab(r.data || {})).catch(()=>{});
    load();
  }, [load]);

  const remove = async (id) => {
    if (!isSuperAdmin(user)) return;
    if (!window.confirm("Delete this market signal permanently?")) return;
    try {
      await api.delete(`/market-signals/${id}`);
      setRows((rs) => rs.filter((r) => r.id !== id));
      toast.success("Deleted.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed.");
    }
  };

  if (!mayWrite) {
    return <div className="p-8 text-sm text-muted-foreground">Available only to authorised internal roles.</div>;
  }

  return (
    <div className="p-6 space-y-6" data-testid="market-signals-desk">
      <div className="flex flex-wrap items-center gap-3">
        <LineChart className="w-6 h-6 text-primary" />
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Market Signals &amp; Catalogue Gaps</h1>
          <p className="text-sm text-muted-foreground max-w-2xl">
            Internal-only workspace for reviewed intelligence. Manual entry — no paid feeds, no external AI, no third-party intelligence services. Human-authored findings are visibly distinct from any system suggestions.
          </p>
        </div>
        <Badge variant="outline" className="text-[10px] uppercase tracking-[0.14em]">Internal-only</Badge>
        <div className="flex-1" />
        <Button variant="outline" size="sm" onClick={load} className="gap-2" data-testid="ms-refresh"><RefreshCw className="w-4 h-4" /> Refresh</Button>
        <CreateSignalDialog vocab={vocab} onCreated={(x)=>setRows((rs)=>[x, ...rs])} />
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Search className="w-4 h-4" /> Filters</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <Label>Search</Label>
              <Input value={q} onChange={(e)=>setQ(e.target.value)} placeholder="summary, source, gap…" data-testid="ms-filter-q" />
            </div>
            <div>
              <Label>Kind</Label>
              <Select value={kind || "__any__"} onValueChange={(v)=>setKind(v === "__any__" ? "" : v)}>
                <SelectTrigger data-testid="ms-filter-kind"><SelectValue placeholder="Any" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__any__">Any kind</SelectItem>
                  {(vocab.kinds || []).map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Confidence</Label>
              <Select value={confidence || "__any__"} onValueChange={(v)=>setConfidence(v === "__any__" ? "" : v)}>
                <SelectTrigger data-testid="ms-filter-confidence"><SelectValue placeholder="Any" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__any__">Any confidence</SelectItem>
                  {(vocab.confidence || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end"><Button onClick={load} className="w-full" data-testid="ms-filter-apply">Apply</Button></div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Signals ({rows.length})</CardTitle>
          <CardDescription className="flex items-center gap-2"><UserCheck className="w-3.5 h-3.5" /> All entries include source and reviewer.</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? <p className="text-sm text-muted-foreground">Loading…</p> :
            rows.length === 0 ? <p className="text-sm text-muted-foreground" data-testid="ms-empty">No signals recorded.</p> :
            <div className="space-y-2">
              {rows.map((r) => (
                <div key={r.id} className="border border-border rounded-md px-4 py-3" data-testid={`ms-row-${r.id}`}>
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="outline" className="text-[10px] uppercase tracking-[0.14em]">{r.kind}</Badge>
                    <Badge variant="outline" className="text-[10px] uppercase tracking-[0.14em]">{r.confidence}</Badge>
                    <OriginBadge value={r.origin} />
                    <span className="font-semibold text-sm flex-1">{r.signal_summary}</span>
                    {isSuperAdmin(user) && (
                      <InlineDeleteButton
                        endpoint={`/market-signals/${r.id}`}
                        recordLabel="Market Signal"
                        recordId={r.id}
                        isSuperAdmin={isSuperAdmin(user)}
                        variant="outline"
                        onDeleted={(id) => setRows((rs) => rs.filter((x) => x.id !== id))}
                        testid={`ms-delete-${r.id}`}
                      />
                    )}
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs mt-2">
                    <div><span className="opacity-60">Source:</span> {r.source || "—"}</div>
                    <div><span className="opacity-60">Source date:</span> {r.source_date || "—"}</div>
                    <div><span className="opacity-60">Reviewer:</span> {r.reviewer_email || "—"}</div>
                    <div><span className="opacity-60">Genre:</span> {r.genre || "—"}</div>
                    <div><span className="opacity-60">Style:</span> {r.style || "—"}</div>
                    <div><span className="opacity-60">Mood:</span> {r.mood || "—"}</div>
                    <div><span className="opacity-60">Instrumentation:</span> {r.instrumentation || "—"}</div>
                    <div><span className="opacity-60">Use case:</span> {r.use_case_relevance || "—"}</div>
                  </div>
                  {(r.observed_demand || r.catalogue_gap || r.recommendation) && (
                    <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
                      {r.observed_demand && <div><span className="opacity-60">Observed demand:</span> {r.observed_demand}</div>}
                      {r.catalogue_gap && <div><span className="opacity-60">Catalogue gap:</span> {r.catalogue_gap}</div>}
                      {r.recommendation && <div><span className="opacity-60">Recommendation:</span> {r.recommendation}</div>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          }
        </CardContent>
      </Card>

      <p className="text-[11px] text-muted-foreground flex items-center gap-2">
        <ShieldAlert className="w-3.5 h-3.5" />
        No automatic recommendations that bypass human review. If a future system suggestion is added, it will appear with the “System suggestion” badge.
      </p>
    </div>
  );
}
